/**
 * The ONE place a location-scoped path is built (the location rename,
 * spec 2026-09-27, Phase 2). Firestore, RTDB and Storage share one shape. Every
 * port (apps/functions, apps/pos, apps/gateway) is tested against
 * __fixtures__/locationScope.cases.json and held to it by the
 * location-scope-parity guard — change the fixture and every port must follow.
 *
 * Until Phase 6 every caller uses the default tree, `legacy`, so output is
 * byte-identical to the hand-built strings it replaces.
 */
import type { LocationScope } from '@order-up/models';
import { ValidationError } from '../errors';

export type StorageTree = 'legacy' | 'location';

// This resolver is the sanctioned home of the literal legacy tree segment;
// every other caller reaches it through the functions below instead.
const SEGMENT = { legacy: 'restaurants', location: 'locations' } as const; // restaurant-ok: legacy tree segment (resolver)

// One rule for a usable segment, shared by the builders (which throw) and the
// reader (which returns null), so a path the builders refuse is never read back.
function isSegment(value: unknown): value is string {
  return typeof value === 'string' && value !== '' && value !== 'undefined' && value !== 'null' && !value.includes('/');
}

function seg(name: string, value: string): string {
  if (!isSegment(value)) {
    throw new ValidationError(`Invalid path segment ${name}: ${JSON.stringify(value)}`);
  }
  return value;
}

export function locationsCollectionId(tree: StorageTree = 'legacy'): 'restaurants' | 'locations' { // restaurant-ok: legacy tree segment (resolver)
  return SEGMENT[tree];
}
export function tenantLocationsPath(tenantDocId: string, tree: StorageTree = 'legacy'): string {
  return `tenants/${seg('tenantDocId', tenantDocId)}/${SEGMENT[tree]}`;
}
export function locationDocPath(scope: LocationScope, tree: StorageTree = 'legacy'): string {
  return `${tenantLocationsPath(scope.tenantDocId, tree)}/${seg('locationDocId', scope.locationDocId)}`;
}
export function locationCollectionPath(scope: LocationScope, collection: string, tree: StorageTree = 'legacy'): string {
  return `${locationDocPath(scope, tree)}/${seg('collection', collection)}`;
}
export function locationSubdocPath(scope: LocationScope, collection: string, docId: string, tree: StorageTree = 'legacy'): string {
  return `${locationCollectionPath(scope, collection, tree)}/${seg('docId', docId)}`;
}
// An ABSENT suffix (undefined) means the location node itself; an EMPTY suffix
// '' is an error, as is any empty segment inside one ('a//b', 'a/') — every port
// follows the shared fixture here.
export function rtdbLocationPath(scope: LocationScope, suffix?: string, tree: StorageTree = 'legacy'): string {
  if (suffix === undefined) return locationDocPath(scope, tree);
  suffix.split('/').forEach((s) => seg('suffix', s));
  return `${locationDocPath(scope, tree)}/${suffix}`;
}
export function rtdbAccessPath(uid: string, scope: LocationScope): string {
  return `rtdb_access/${seg('uid', uid)}/${seg('tenantDocId', scope.tenantDocId)}/${seg('locationDocId', scope.locationDocId)}`;
}
export function storageLocationPrefix(scope: LocationScope, tree: StorageTree = 'legacy'): string {
  return locationDocPath(scope, tree);
}
export function locationCollectionGroupId(collection: string): string {
  return seg('collection', collection);
}

/**
 * The inverse of the builders above: the location a Firestore document lives
 * under, read from its path. For a collection-group query, whose documents come
 * back from every location at once and carry no trustworthy copy of their own
 * scope — where a document lives is the one fact that cannot disagree with
 * itself. Null when the path is not `tenants/{t}/{tree segment}/{l}[/...]` in
 * the given tree, or any segment is one the builders refuse, so a caller skips
 * the document rather than guessing its owner.
 */
export function locationScopeFromPath(path: string, tree: StorageTree = 'legacy'): LocationScope | null {
  if (typeof path !== 'string') return null;
  const parts = path.split('/');
  if (parts.length < 4 || parts[0] !== 'tenants' || parts[2] !== SEGMENT[tree] || !parts.every(isSegment)) {
    return null;
  }
  return { tenantDocId: parts[1], locationDocId: parts[3] };
}
